import React, { useState } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { Bell, X, Check, Trash2, Filter, Zap, Play, Settings, AlertTriangle, Trophy, Vote, DollarSign, Shield } from 'lucide-react';

const NotificationCenter: React.FC = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    removeNotification, 
    clearAll,
    triggerDemoNotifications,
    simulateRacingWin,
    simulateGovernanceUpdate,
    simulateTreasuryUpdate,
    simulateSecurityAlert
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'racing' | 'governance' | 'treasury' | 'security'>('all');
  const [showDemoControls, setShowDemoControls] = useState(false);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'racing': return '🏇';
      case 'governance': return '🗳️';
      case 'treasury': return '💰';
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'security': return '🔒';
      case 'system': return '⚙️';
      default: return 'ℹ️';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-red-500/50 bg-red-500/10';
      case 'high': return 'border-orange-500/50 bg-orange-500/10';
      case 'medium': return 'border-blue-500/50 bg-blue-500/10';
      case 'low': return 'border-slate-500/50 bg-slate-500/10';
      default: return 'border-slate-500/50 bg-slate-500/10';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <AlertTriangle className="w-3 h-3 text-red-400" />;
      case 'high': return <AlertTriangle className="w-3 h-3 text-orange-400" />;
      case 'medium': return <Bell className="w-3 h-3 text-blue-400" />;
      case 'low': return <Bell className="w-3 h-3 text-slate-400" />;
      default: return <Bell className="w-3 h-3 text-slate-400" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'racing': return 'border-green-500/30 bg-green-500/10';
      case 'governance': return 'border-blue-500/30 bg-blue-500/10';
      case 'treasury': return 'border-cyan-500/30 bg-cyan-500/10';
      case 'success': return 'border-green-500/30 bg-green-500/10';
      case 'error': return 'border-red-500/30 bg-red-500/10';
      case 'warning': return 'border-yellow-500/30 bg-yellow-500/10';
      default: return 'border-slate-500/30 bg-slate-500/10';
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <>
      {/* Notification Bell */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-slate-300 hover:text-cyan-400 hover:bg-slate-800/50 transition-all duration-200"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-end p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white">Notifications</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Filter Tabs */}
              <div className="flex space-x-1 bg-slate-700/50 rounded-lg p-1">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'unread', label: 'Unread' },
                  { key: 'racing', label: 'Racing' },
                  { key: 'governance', label: 'Governance' },
                  { key: 'treasury', label: 'Treasury' }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setFilter(tab.key as any)}
                    className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      filter === tab.key
                        ? 'bg-cyan-500 text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={markAllAsRead}
                  className="flex items-center space-x-2 px-3 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                >
                  <Check className="w-4 h-4" />
                  <span>Mark All Read</span>
                </button>
                <button
                  onClick={clearAll}
                  className="flex items-center space-x-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Clear All</span>
                </button>
                <button
                  onClick={() => setShowDemoControls(!showDemoControls)}
                  className="flex items-center space-x-2 px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm"
                >
                  <Zap className="w-4 h-4" />
                  <span>Demo Mode</span>
                </button>
              </div>

              {/* Demo Controls */}
              {showDemoControls && (
                <div className="mt-4 p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <h4 className="text-white font-semibold mb-3">Demo Notifications</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={triggerDemoNotifications}
                      className="flex items-center space-x-2 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors text-sm"
                    >
                      <Play className="w-4 h-4" />
                      <span>Full Demo</span>
                    </button>
                    <button
                      onClick={simulateRacingWin}
                      className="flex items-center space-x-2 px-3 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors text-sm"
                    >
                      <Trophy className="w-4 h-4" />
                      <span>Racing Win</span>
                    </button>
                    <button
                      onClick={simulateGovernanceUpdate}
                      className="flex items-center space-x-2 px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm"
                    >
                      <Vote className="w-4 h-4" />
                      <span>Governance</span>
                    </button>
                    <button
                      onClick={simulateTreasuryUpdate}
                      className="flex items-center space-x-2 px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm"
                    >
                      <DollarSign className="w-4 h-4" />
                      <span>Treasury</span>
                    </button>
                    <button
                      onClick={simulateSecurityAlert}
                      className="flex items-center space-x-2 px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm col-span-2"
                    >
                      <Shield className="w-4 h-4" />
                      <span>Security Alert</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-96">
              {filteredNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 hover:bg-slate-700/50 transition-colors border-l-4 ${
                        !notification.read ? 'bg-slate-700/30' : ''
                      } ${getPriorityColor(notification.priority)}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="text-2xl">{getNotificationIcon(notification.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h3 className="text-white font-medium">{notification.title}</h3>
                                {getPriorityIcon(notification.priority)}
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                                )}
                              </div>
                              <p className="text-slate-400 text-sm mt-1">{notification.message}</p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-slate-500 text-xs">{formatTimeAgo(notification.timestamp)}</p>
                                {notification.metadata && (
                                  <div className="flex items-center space-x-2">
                                    {notification.metadata.amount && (
                                      <span className="text-green-400 text-xs font-medium">
                                        {notification.metadata.amount}
                                      </span>
                                    )}
                                    {notification.metadata.horseName && (
                                      <span className="text-blue-400 text-xs">
                                        {notification.metadata.horseName}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 ml-2">
                              <button
                                onClick={() => removeNotification(notification.id)}
                                className="p-1 rounded text-slate-400 hover:text-red-400 hover:bg-red-500/20 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {notification.action && (
                            <button
                              onClick={() => {
                                notification.action?.onClick();
                                markAsRead(notification.id);
                              }}
                              className="mt-3 text-cyan-400 hover:text-cyan-300 text-sm font-medium flex items-center space-x-1"
                            >
                              <span>{notification.action.label}</span>
                              <span>→</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationCenter;
